// Вокабуляры базы `weern_work` + МОСТ в вокабуляр каталога (2026-08-14).
//
// Здесь живёт единственное место, где «категория work» превращается в
// «категорию каталога». Раньше эта таблица была скопирована в ДВУХ файлах
// воркера (`indexer.ts` CAT_MAP и `print.ts` WORK_TO_CATALOG_CAT) — две
// копии одного знания, ничем не связанные.
import { z } from 'zod';
import { AssetCategorySchema, type AssetCategory } from './catalog';

// ─── Категория в work_photos ────────────────────────────────────────────────
// ВНИМАНИЕ: колонка `work_photos.category` хранит ДВА РАЗНЫХ ВОКАБУЛЯРА.
//   • камера кабинета пишет множественное число: tickets / coupons / giftcards
//     (WorkCaptureCategorySchema — контракт входа `uploadFrame`);
//   • путь генератора (`generator_upload.ts` VALID_CATEGORIES) пишет
//     КАТАЛОЖНОЕ написание: membership / gift_card / coupon / event_ticket.
//     Так в колонке появились 136 строк `membership` (июль 2026).
// Это не дизайн, это расхождение двух путей записи. Пока оно живо, читатель
// колонки обязан принимать оба написания — для этого есть toCatalogCategory().
export const WORK_PHOTO_CATEGORY = [
  'tickets', 'coupons', 'giftcards', 'membership', 'receipts',
] as const;
export const WorkPhotoCategorySchema = z.enum(WORK_PHOTO_CATEGORY);
export type WorkPhotoCategory = z.infer<typeof WorkPhotoCategorySchema>;

// ─── Состояние авто-eval кадра ──────────────────────────────────────────────
// В проде живут только `auto_passed` и `needs_qa`: `pending` — стартовое,
// перезаписывается тем же запросом, а `rejected` не встречается ни разу,
// потому что авто-реджект выключен (autoreject OFF). Оба оставлены в
// вокабуляре: их пишет код, и включение авто-реджекта не должно требовать
// правки контракта.
export const WORK_EVAL_STATE = ['pending', 'auto_passed', 'needs_qa', 'rejected'] as const;
export const WorkEvalStateSchema = z.enum(WORK_EVAL_STATE);
export type WorkEvalState = z.infer<typeof WorkEvalStateSchema>;

// ─── Вердикт ручной проверки ────────────────────────────────────────────────
// '' = не смотрели ЛИБО приняли: приём кадра отдельного значения не пишет.
// Поэтому «принят» ≠ review='accepted', а review='' И не rejected.
export const WORK_REVIEW = ['', 'rejected'] as const;
export const WorkReviewSchema = z.enum(WORK_REVIEW);
export type WorkReview = z.infer<typeof WorkReviewSchema>;

// ─── МОСТ: категория work → категория каталога ──────────────────────────────
// `satisfies Record<WorkPhotoCategory, AssetCategory>` — то, ради чего всё
// затевалось: добавишь значение в WORK_PHOTO_CATEGORY и забудешь строку тут —
// НЕ СОБЕРЁТСЯ. Раньше карта была объектом без типа, и пропущенный ключ молча
// отдавал исходную строку в каталог.
export const WORK_TO_CATALOG_CATEGORY = {
  tickets: 'event_ticket',
  coupons: 'coupon',
  giftcards: 'gift_card',
  membership: 'membership',
  // Чеки из кабинета App (2026-08-25). До этой строки toCatalogCategory
  // честно отдавал '' — и чек ложился в каталог пустой категорией,
  // неотличимо от разметочных дыр scrape-intake.
  receipts: 'receipt',
} as const satisfies Record<WorkPhotoCategory, AssetCategory>;

/**
 * Категория work → категория каталога.
 *
 * Принимает ОБА написания, живущих в колонке (см. коммент к
 * WORK_PHOTO_CATEGORY): множественное число от камеры и каталожное от
 * генератора. Неизвестное значение даёт '' — ПУСТО, а не исходную строку:
 * мусор в `pothos_assets.category` тише и дороже, чем явная дыра (её видно
 * в pack_issues, и кадр не уедет к заказчику незамеченным).
 */
export function toCatalogCategory(raw: string): AssetCategory | '' {
  const s = String(raw || '').trim();
  if (s in WORK_TO_CATALOG_CATEGORY) {
    return WORK_TO_CATALOG_CATEGORY[s as WorkPhotoCategory];
  }
  const direct = AssetCategorySchema.safeParse(s);
  return direct.success ? direct.data : '';
}

/** Формат фрейма мастера (дизайнерский цикл на work, ADR-0055):
 *  original — размер исходника; iphone15 — 1179×2556; pdf — А4 (595×842).
 *  Сохраняется с версией мастера, следующее открытие стартует в нём. */
export const WorkMasterFormatSchema = z.enum(['original', 'iphone15', 'pdf']);
export type WorkMasterFormat = z.infer<typeof WorkMasterFormatSchema>;

// ─── Статус сданного кадра дизайнера (`des_deliverables`, мигр. 0063/0069) ──
// Жизненный цикл: pending (лёг в postScreens) → submitted (в очереди сверки:
// finalizeShipment, а перезалив реворка старой версии — сразу) → final
// (сверка совпала) → indexed (строка каталога pothos_assets, дальше вердикт
// QA); rejected — вернул автомат сверки или QA, причина в notes. 'failed' —
// наследие порта pothos: колонку никто не пишет (прод 2026-09-01: 0 строк из
// 1269), finalizeShipment лишь перечитывает его в очередь; в вокабуляре
// хранения остаётся, потому что DDL 0063 его объявляет.
export const DES_DELIVERABLE_STATUS = [
  'pending', 'submitted', 'final', 'indexed', 'rejected', 'failed',
] as const;
export const DesDeliverableStatusSchema = z.enum(DES_DELIVERABLE_STATUS);
export type DesDeliverableStatus = z.infer<typeof DesDeliverableStatusSchema>;

// «Живые» статусы: кадр на сервере / в сверке / в QA. Для ячейки и батча
// это «отправлено» (designTasks прячет ячейку, designManifest красит батч
// агрегатора через sent_display_ids). rejected сюда НЕ входит — отклонённый
// кадр ждёт перезалива. До 2026-09-02 тот же список лежал литералом в двух
// SQL-строках work-api и мог разойтись молча.
export const DES_DELIVERABLE_LIVE_STATUSES = [
  'pending', 'submitted', 'final', 'indexed',
] as const satisfies readonly DesDeliverableStatus[];
